<script setup>
/**
 * What this workspace is allowed, read-only, with the operator's fingerprints on it.
 *
 * EVERY ROW HERE IS A LABEL AND NOT A CONTROL, on purpose. Some of these numbers are
 * platform defaults and some were pinned for this workspace by us; none of them are
 * writable by the workspace, and the settings PATCH does not carry them at all (it takes
 * four fields and rebuilds its upstream patch field by field). A number box that silently
 * writes nowhere is worse than a sentence saying who owns the number.
 *
 * `operatorSet` CARRIES SEVEN BOOLEANS WHERE `limits` CARRIES NINE FIELDS, and the two
 * missing ones are not an oversight: `resumeKeywords` and `retentionDays` have no
 * per-workspace override column at all, so there is nothing for an operator to have set
 * and no "Set by Waslo" to show against them.
 *
 * THREE VALUES THAT LOOK LIKE "NOTHING" AND MEAN THREE DIFFERENT THINGS, which is the
 * entire reason this card is a component rather than a loop over a table:
 *
 *   • `monthlyMessagesOverride: null` — we impose no monthly limit of our own.
 *   • `monthlyMessagesOverride: 0`    — an operator has deliberately STOPPED this
 *     workspace for the month. It must never render as "not set"; a `||` anywhere near
 *     this value would report the platform default while the counters applied the zero.
 *   • `allowedCountryPrefixes: []`    — an empty list is not "anywhere", it is NOWHERE,
 *     and a workspace in that state can send nothing at all. `null` is "anywhere".
 *
 * Numbers go through `Intl.NumberFormat` for the same reason the rest of the section
 * does: an Arabic account reads Arabic-Indic digits, and the locale has to survive
 * Chatwoot's non-BCP-47 directory names (`helpers/intl.js`).
 */
import { computed } from 'vue';

import { useWasloI18n } from '../../i18n.js';
import { toIntlLocale } from '../../helpers/intl.js';

const props = defineProps({
  /** `settings.limits` — every field, exactly as the DTO reads it. */
  limits: { type: Object, required: true },
  /** `settings.operatorSet` — the seven booleans. */
  operatorSet: { type: Object, required: true },
});

const { t, locale } = useWasloI18n();

const format = computed(
  () => new Intl.NumberFormat(toIntlLocale(locale.value))
);

const count = value => format.value.format(value);

/**
 * Null is "anywhere", `[]` is NOWHERE, and a non-empty list is rendered as chips with no
 * sentence at all. Written out rather than nested inline so the three cases are visibly
 * three cases.
 */
const countries = computed(() => {
  const value = props.limits.allowedCountryPrefixes;
  if (value === null) return t('WASLO.OUTBOUND.SETUP.LIMITS.COUNTRIES_ANY');
  if (value.length === 0)
    return t('WASLO.OUTBOUND.SETUP.LIMITS.COUNTRIES_NONE');
  return '';
});

const monthly = computed(() => {
  const value = props.limits.monthlyMessagesOverride;
  if (value === null)
    return t('WASLO.OUTBOUND.SETUP.LIMITS.MONTHLY_MESSAGES_NONE');
  if (value === 0)
    return t('WASLO.OUTBOUND.SETUP.LIMITS.MONTHLY_MESSAGES_ZERO');
  return t('WASLO.OUTBOUND.SETUP.LIMITS.PER_MONTH', { count: count(value) });
});

/**
 * The rows, in the order they matter to somebody wondering why a send was refused: the
 * rates first, then the month, then who may be messaged, then the words that stop and
 * start it, then how long the record lasts.
 *
 * `list` rows render their values as chips; `text` rows render one sentence. A row whose
 * `operator` is true wears the "Set by Waslo" mark and its hint.
 */
const rows = computed(() => [
  {
    key: 'ratePerMin',
    label: t('WASLO.OUTBOUND.SETUP.LIMITS.RATE_PER_MIN'),
    text: t('WASLO.OUTBOUND.SETUP.LIMITS.PER_MINUTE', {
      count: count(props.limits.ratePerMin),
    }),
    operator: props.operatorSet.ratePerMin,
  },
  {
    key: 'ratePerDay',
    label: t('WASLO.OUTBOUND.SETUP.LIMITS.RATE_PER_DAY'),
    text: t('WASLO.OUTBOUND.SETUP.LIMITS.PER_DAY', {
      count: count(props.limits.ratePerDay),
    }),
    operator: props.operatorSet.ratePerDay,
  },
  {
    key: 'readRatePerDay',
    label: t('WASLO.OUTBOUND.SETUP.LIMITS.READ_RATE_PER_DAY'),
    text: t('WASLO.OUTBOUND.SETUP.LIMITS.PER_DAY', {
      count: count(props.limits.readRatePerDay),
    }),
    hint: t('WASLO.OUTBOUND.SETUP.LIMITS.READ_RATE_PER_DAY_HINT'),
    operator: props.operatorSet.readRatePerDay,
  },
  {
    key: 'recipientCapPerDay',
    label: t('WASLO.OUTBOUND.SETUP.LIMITS.RECIPIENT_CAP_PER_DAY'),
    text: t('WASLO.OUTBOUND.SETUP.LIMITS.PER_DAY', {
      count: count(props.limits.recipientCapPerDay),
    }),
    operator: props.operatorSet.recipientCapPerDay,
  },
  {
    key: 'monthlyMessages',
    label: t('WASLO.OUTBOUND.SETUP.LIMITS.MONTHLY_MESSAGES'),
    text: monthly.value,
    operator: props.operatorSet.monthlyMessagesOverride,
  },
  {
    key: 'countries',
    label: t('WASLO.OUTBOUND.SETUP.LIMITS.COUNTRIES'),
    // Null and [] are opposite answers; see the block comment above.
    text: countries.value,
    list: props.limits.allowedCountryPrefixes ?? [],
    operator: props.operatorSet.allowedCountryPrefixes,
  },
  {
    key: 'stopWords',
    label: t('WASLO.OUTBOUND.SETUP.LIMITS.STOP_WORDS'),
    text:
      props.limits.optOutKeywords.length === 0
        ? t('WASLO.OUTBOUND.COMMON.NONE')
        : '',
    list: props.limits.optOutKeywords,
    operator: props.operatorSet.optOutKeywords,
  },
  {
    key: 'startWords',
    label: t('WASLO.OUTBOUND.SETUP.LIMITS.START_WORDS'),
    text:
      props.limits.resumeKeywords.length === 0
        ? t('WASLO.OUTBOUND.COMMON.NONE')
        : '',
    list: props.limits.resumeKeywords,
    // No override column exists for this one — see above.
    operator: false,
  },
  {
    key: 'retention',
    label: t('WASLO.OUTBOUND.SETUP.LIMITS.RETENTION'),
    text: t('WASLO.OUTBOUND.SETUP.LIMITS.DAYS', {
      count: count(props.limits.retentionDays),
    }),
    operator: false,
  },
]);
</script>

<template>
  <div
    class="flex flex-col gap-4 p-5 outline outline-1 -outline-offset-1 outline-n-container rounded-xl bg-n-solid-2"
    data-waslo-outbound-limits
  >
    <!--
      A definition list, not a table: at panel width a two-column table either scrolls
      sideways or crushes one column to nothing (README §1). Each row stacks its label
      above its value below `sm` and sits side by side above it.
    -->
    <dl class="flex flex-col gap-4 mb-0">
      <div
        v-for="row in rows"
        :key="row.key"
        class="flex flex-col gap-1 sm:flex-row sm:items-start sm:gap-6"
        :data-waslo-outbound-limit="row.key"
      >
        <dt class="text-sm text-n-slate-11 sm:w-56 sm:shrink-0">
          {{ row.label }}
        </dt>
        <dd class="flex flex-col min-w-0 gap-1 mb-0 sm:flex-1">
          <div class="flex flex-wrap items-center min-w-0 gap-2">
            <span
              v-if="row.text"
              class="text-sm text-n-slate-12"
              data-waslo-outbound-limit-value
            >
              {{ row.text }}
            </span>
            <!--
              Chips for the list-valued rows. They wrap, and each one is `break-all`: a
              country prefix is short, but a stop word is whatever the operator typed.
            -->
            <span
              v-for="item in row.list ?? []"
              :key="item"
              class="inline-flex items-center h-6 px-2 text-xs break-all rounded-md bg-n-alpha-2 text-n-slate-12"
            >
              <bdi>{{ item }}</bdi>
            </span>
            <span
              v-if="row.operator"
              class="inline-flex items-center h-6 px-2 text-xs rounded-md shrink-0 bg-n-blue-3 text-n-blue-11"
              data-waslo-outbound-limit-operator
            >
              {{ t('WASLO.OUTBOUND.SETUP.LIMITS.SET_BY_WASLO') }}
            </span>
          </div>
          <p v-if="row.hint" class="mb-0 text-xs leading-5 text-n-slate-10">
            {{ row.hint }}
          </p>
          <p
            v-if="row.operator"
            class="mb-0 text-xs leading-5 text-n-slate-10"
            data-waslo-outbound-limit-operator-hint
          >
            {{ t('WASLO.OUTBOUND.SETUP.LIMITS.SET_BY_WASLO_HINT') }}
          </p>
        </dd>
      </div>
    </dl>
  </div>
</template>
